import { useRef } from 'react';
import Script from 'next/script';
import Handlebars from 'handlebars';
import { useCountdowns } from '../../hooks/useCountdowns';
import { countdownScriptContent, registerCountdownHelper } from '../../utils/amplience/countdown';
import { startsWithLocale } from '../../utils/internationalization';

export type HandlebarTemplateProps = {
  template: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: Record<string, any>;
};

type UrlObjectType = {
  url?: string;
  internalUrl?: string;
  externalUrl?: string;
};

export function HandlebarTemplate({ template, input }: HandlebarTemplateProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const countdowns = useCountdowns(containerRef);

  try {
    Handlebars.registerHelper('getHrefFromLinkObject', (locale?: string, urlObject: UrlObjectType = {}) => {
      const { url, internalUrl, externalUrl } = urlObject;

      let urlStr = '/';
      if (externalUrl) {
        urlStr = externalUrl;
      } else if (internalUrl) {
        urlStr = startsWithLocale(internalUrl) || !locale ? `/${internalUrl}` : `/${locale}/${internalUrl}`;
      } else if (url) {
        urlStr = url;
      }

      return new Handlebars.SafeString(` href=${urlStr}`);
    });

    Handlebars.registerHelper(
      'getFirstTruthyString',
      (...args: Array<unknown>) => args.find((arg) => typeof arg === 'string' && arg?.trim() !== '') ?? '',
    );

    registerCountdownHelper();

    // Partial that invokes the helper function, since helper functions break Amplience visualizations for business components
    // A corresponding partial b-partial-get-href is created in Amplience Content Hub
    // but it is only used for the Amplience visualization, the actual rendering logic lives here
    Handlebars.registerPartial('b-partial-get-href', `{{getHrefFromLinkObject locale urlObject}}`);

    const compiledTemplate = Handlebars.compile(template);

    return (
      <>
        <div
          ref={containerRef}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: compiledTemplate(input) }}
        />
        {countdowns.map((countdown) => {
          // Generate unique key to ensure script re-execution after navigation
          const uniqueKey = `${countdown.id}-${Math.random().toString(36).substring(2, 6)}`;

          return (
            <Script
              key={uniqueKey}
              id={`countdown-script-${uniqueKey}`}
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: countdownScriptContent(countdown.id, countdown.showSeconds),
              }}
            />
          );
        })}
      </>
    );
  } catch (e) {
    console.error('There was an error compiling your Handlebars template', e);
    return null;
  }
}
